import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetAlertsQuery } from "store/api/alertsApi"
import { useAppSelector } from "store/hooks"
import { UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { WidgetAlerts } from "components/widgets/WidgetAlerts"
import { WidgetAlertStatuses } from "components/widgets/WidgetAlertStatuses"

import { AlertsPageContent } from "./AlertsPageContent"
import { dummyAlerts, dummyMeta } from "./dummyAlertsData"

import styles from "./index.module.scss"

const AlertsPage = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [triggerGetAlerts, { isFetching, data }] = useLazyGetAlertsQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const [updateIndex, setUpdateIndex] = useState(0)

  const hasAlertsAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Alerts) : false

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAlerts(params)
      const error = getErrorMessage(res?.error)
      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    fetchAlerts()
  }, [urlQueryParams, isFirstRender])

  const fetchAlerts = () => {
    if (hasAlertsAccess) {
      sendRequest(urlQueryParams)
    }
  }

  const getAlerts = () => {
    if (!hasAlertsAccess) {
      return dummyAlerts
    }

    return data?.data || []
  }

  const handleRefreshAlerts = () => {
    fetchAlerts()
    setUpdateIndex(prev => prev + 1)
  }

  const alerts = getAlerts()
  const meta = hasAlertsAccess ? data?.meta : dummyMeta
  const isFetchingData = isFetching && !data

  return (
    <>
      <div className={classNames(styles.widgetsWrapper)}>
        <WidgetAlerts hasAlertsAccess={hasAlertsAccess} updateIndex={updateIndex} />
        <WidgetAlertStatuses hasAlertsAccess={hasAlertsAccess} updateIndex={updateIndex} />
      </div>
      <AlertsPageContent
        alertsList={alerts}
        isFetching={isFetchingData}
        meta={meta}
        onRefreshAlerts={handleRefreshAlerts}
      />
      {!hasAlertsAccess && <SubscriptionUpgradeModal page={SubscriptionPrivilege.Alerts} />}
    </>
  )
}

export default AlertsPage
